import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, DollarSign, Mail, Phone } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Colors for the pie chart segments
const COLORS = ['#d97706', '#0891b2', '#7c3aed', '#059669', '#dc2626', '#2563eb', '#c026d3', '#ea580c'];

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

function ViewerPartnersView({ llcGroupedData, viewMode = 'cards' }) {
  if (!llcGroupedData || llcGroupedData.length === 0) {
    return (
      <Card className="border-dashed border-2 border-slate-300">
        <CardContent className="py-12 text-center">
          <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="font-serif text-lg font-semibold text-slate-700">No LLCs found</h3>
          <p className="text-slate-500 mt-1">You do not have ownership in any LLCs yet</p>
        </CardContent>
      </Card>
    );
  }

  // List View - Grouped by LLC
  if (viewMode === 'list') {
    const llcSections = [];
    for (let i = 0; i < llcGroupedData.length; i++) {
      const llc = llcGroupedData[i];
      
      const partnerRows = [];
      for (let j = 0; j < llc.partners.length; j++) {
        const partner = llc.partners[j];
        partnerRows.push(
          <tr key={partner.id} className={`hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${j % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
            <td className="px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-sm font-semibold text-white shadow-sm">
                  {partner.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-slate-900 font-medium">{partner.name}</span>
              </div>
            </td>
            <td className="px-4 py-3">
              <a href={`mailto:${partner.email}`} className="text-slate-700 hover:text-amber-700 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-slate-400" />
                {partner.email}
              </a>
            </td>
            <td className="px-4 py-3">
              {partner.phone ? (
                <a href={`tel:${partner.phone}`} className="text-slate-700 hover:text-amber-700 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  {partner.phone}
                </a>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </td>
            <td className="px-4 py-3 text-right">
              <span className="font-mono text-base font-bold text-amber-700">
                {llc.hide_percentages ? <span className="text-slate-400 italic font-normal">Calculated</span> : `${Number(partner.percentage).toFixed(2)}%`}
              </span>
            </td>
          </tr>
        );
      }

      llcSections.push(
        <Card key={llc.llc_id} className="border-slate-200 overflow-hidden shadow-sm">
          {/* LLC Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-serif font-semibold text-lg">{llc.llc_name}</h3>
                <p className="text-slate-300 text-sm">{llc.partners.length} partner{llc.partners.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <Badge className={`font-mono text-sm px-3 py-1.5 font-semibold shadow-sm ${
              llc.total_assets >= 0 
                ? 'bg-emerald-500 text-white' 
                : 'bg-rose-500 text-white'
            }`}>
              {formatCurrency(llc.total_assets)}
            </Badge>
          </div>
          {/* Partners Table */}
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 border-b-2 border-slate-200">
                <th className="text-slate-700 font-bold text-left px-4 py-3 text-sm uppercase tracking-wide">Partner</th>
                <th className="text-slate-700 font-bold text-left px-4 py-3 text-sm uppercase tracking-wide">Email</th>
                <th className="text-slate-700 font-bold text-left px-4 py-3 text-sm uppercase tracking-wide">Phone</th>
                <th className="text-slate-700 font-bold text-right px-4 py-3 text-sm uppercase tracking-wide">Ownership</th>
              </tr>
            </thead>
            <tbody>{partnerRows}</tbody>
          </table>
        </Card>
      );
    }

    return <div className="space-y-6">{llcSections}</div>;
  }

  // Cards View (default)
  const allCards = [];
  for (let i = 0; i < llcGroupedData.length; i++) {
    const llc = llcGroupedData[i];
    
    // Prepare data for pie chart
    const pieData = [];
    for (let j = 0; j < llc.partners.length; j++) {
      pieData.push({
        name: llc.partners[j].name,
        value: Number(llc.partners[j].percentage),
        email: llc.partners[j].email
      });
    }

    // Build legend items
    const legendItems = [];
    for (let j = 0; j < llc.partners.length; j++) {
      const partner = llc.partners[j];
      const color = COLORS[j % COLORS.length];
      legendItems.push(
        <div key={partner.id} className="flex items-center gap-2 py-1">
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0" 
            style={{ backgroundColor: color }}
          />
          <span className="text-sm text-slate-700 truncate flex-1">{partner.name}</span>
          <span className="text-sm font-mono font-medium text-slate-900">
            {llc.hide_percentages ? <span className="text-slate-400 italic font-normal">Calc</span> : `${Number(partner.percentage).toFixed(2)}%`}
          </span>
        </div>
      );
    }

    allCards.push(
      <Card key={llc.llc_id} className="border-slate-200">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-700 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <CardTitle className="font-serif text-lg">{llc.llc_name}</CardTitle>
            </div>
            <Badge className="bg-emerald-100 text-emerald-800 font-mono text-sm px-2 py-1">
              <DollarSign className="w-3 h-3 mr-0.5" />
              {formatCurrency(llc.total_assets)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0 pb-4 px-4">
          <div className="flex items-center gap-4">
            {/* Pie Chart */}
            <div className="w-28 h-28 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={45}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  {!llc.hide_percentages && <Tooltip 
                    formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                    labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                    itemStyle={{ color: '#475569' }}
                  />}
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex-1 min-w-0">
              {legendItems}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {allCards}
    </div>
  );
}

export default ViewerPartnersView;
